/**
 * PlantasIotAvisosTab.jsx  v1
 * Pestaña "Avisos" del engranaje de Plantas IoT
 * Horario de envío diario, destinatarios (SMS / mail), vista previa, prueba y registro de envíos
 */

import { useState, useEffect, useCallback } from 'react';
import {
    Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, FormControl, FormControlLabel, IconButton, MenuItem, Paper, Select, Stack, Switch,
    Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Save as SaveIcon,
    Send as SendIcon,
    Visibility as PreviewIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

const HORA_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

const DEST_VACIO = {
    f_nombre: '', f_celular: '', f_email: '',
    recibe_sms: false, recibe_mail: false, activo: true,
};

function fmtFecha(iso) {
    return iso ? new Date(iso).toLocaleString('es-AR', { hour12: false }) : '—';
}

const aFila = (d) => ({
    ...d,
    f_nombre: d.nombre || '',
    f_celular: d.celular || '',
    f_email: d.email || '',
    _dirty: false,
});

// Body para el backend: la casilla solo vale si el campo tiene algo
const cuerpoDest = (d) => ({
    nombre: d.f_nombre.trim(),
    celular: d.f_celular.trim() || null,
    email: d.f_email.trim() || null,
    recibe_sms: d.f_celular.trim() !== '' && d.recibe_sms,
    recibe_mail: d.f_email.trim() !== '' && d.recibe_mail,
    activo: d.activo,
});

// Al editar: si el celular o el mail quedan vacíos, se desmarca su casilla
function aplicarCambio(d, campo, valor) {
    const n = { ...d, [campo]: valor, _dirty: true };
    if (campo === 'f_celular' && !String(valor).trim()) n.recibe_sms = false;
    if (campo === 'f_email' && !String(valor).trim()) n.recibe_mail = false;
    return n;
}

export default function PlantasIotAvisosTab({ plantas, apiAdmin }) {
    const { enqueueSnackbar } = useSnackbar();

    const [plantaId, setPlantaId] = useState(plantas[0]?.id || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [config, setConfig] = useState({ activo: false, hora: '18:00', _dirty: false });
    const [dest, setDest] = useState([]);
    const [nuevo, setNuevo] = useState(DEST_VACIO);

    const [envios, setEnvios] = useState([]);
    const [loadingEnv, setLoadingEnv] = useState(false);

    const [confirmarBorrar, setConfirmarBorrar] = useState(null); // destinatario
    const [enviando, setEnviando] = useState(null);               // 'todos' | id
    const [preview, setPreview] = useState(null);                 // { sms, asunto, html }
    const [cargandoPreview, setCargandoPreview] = useState(false);

    // ── Carga ──────────────────────────────────────────────────────────────────
    const cargar = useCallback(async () => {
        if (!plantaId) return;
        setLoading(true);
        setError(null);
        try {
            const r = await apiAdmin(`/plantas/${plantaId}/avisos`);
            setConfig({
                activo: r.config.activo,
                hora: String(r.config.horas || '18:00').split(',')[0],
                _dirty: false,
            });
            setDest(r.destinatarios.map(aFila));
            setNuevo(DEST_VACIO);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [apiAdmin, plantaId]);

    const cargarEnvios = useCallback(async () => {
        if (!plantaId) return;
        setLoadingEnv(true);
        try {
            setEnvios(await apiAdmin(`/plantas/${plantaId}/envios?limit=20`));
        } catch (err) {
            enqueueSnackbar(err.message, { variant: 'error' });
        } finally {
            setLoadingEnv(false);
        }
    }, [apiAdmin, plantaId, enqueueSnackbar]);

    useEffect(() => {
        cargar();
        cargarEnvios();
    }, [cargar, cargarEnvios]);

    // ── Configuración ──────────────────────────────────────────────────────────
    const horaValida = HORA_RE.test(config.hora);

    async function guardarConfig() {
        try {
            await apiAdmin(`/plantas/${plantaId}/avisos`, 'PUT', { activo: config.activo, horas: config.hora });
            setConfig((c) => ({ ...c, _dirty: false }));
            enqueueSnackbar(
                config.activo ? `Avisos activos: todos los días a las ${config.hora}` : 'Avisos desactivados',
                { variant: 'success' }
            );
        } catch (err) {
            enqueueSnackbar(err.message, { variant: 'error' });
        }
    }

    // ── Destinatarios ──────────────────────────────────────────────────────────
    const editarDest = (id, campo, valor) =>
        setDest((ds) => ds.map((d) => (d.id === id ? aplicarCambio(d, campo, valor) : d)));

    async function guardarDest(d) {
        try {
            await apiAdmin(`/plantas/${plantaId}/destinatarios/${d.id}`, 'PUT', cuerpoDest(d));
            setDest((ds) => ds.map((x) => (x.id === d.id ? { ...x, _dirty: false } : x)));
            enqueueSnackbar(`Destinatario "${d.f_nombre}" guardado`, { variant: 'success' });
        } catch (err) {
            enqueueSnackbar(err.message, { variant: 'error' });
        }
    }

    async function crearDest() {
        try {
            await apiAdmin(`/plantas/${plantaId}/destinatarios`, 'POST', cuerpoDest(nuevo));
            enqueueSnackbar(`Destinatario "${nuevo.f_nombre}" agregado`, { variant: 'success' });
            cargar();
        } catch (err) {
            enqueueSnackbar(err.message, { variant: 'error' });
        }
    }

    async function borrarDest(d) {
        setConfirmarBorrar(null);
        try {
            await apiAdmin(`/plantas/${plantaId}/destinatarios/${d.id}`, 'DELETE');
            setDest((ds) => ds.filter((x) => x.id !== d.id));
            enqueueSnackbar(`Destinatario "${d.f_nombre}" borrado`, { variant: 'success' });
        } catch (err) {
            enqueueSnackbar(err.message, { variant: 'error' });
        }
    }

    const puedeCrear = nuevo.f_nombre.trim() !== '' && (nuevo.f_celular.trim() !== '' || nuevo.f_email.trim() !== '');

    // ── Prueba y vista previa ──────────────────────────────────────────────────
    async function probar(destinatarioId) {
        setEnviando(destinatarioId || 'todos');
        try {
            const r = await apiAdmin(
                `/plantas/${plantaId}/avisos/probar`, 'POST',
                destinatarioId ? { destinatarioId } : {}
            );
            if (r.aviso) {
                enqueueSnackbar(r.aviso, { variant: 'warning' });
            } else {
                enqueueSnackbar(
                    `Prueba: ${r.enviados} enviado(s), ${r.errores} con error`,
                    { variant: r.errores ? 'warning' : 'success' }
                );
            }
            cargarEnvios();
        } catch (err) {
            enqueueSnackbar(err.message, { variant: 'error' });
        } finally {
            setEnviando(null);
        }
    }

    async function verPreview() {
        setCargandoPreview(true);
        try {
            setPreview(await apiAdmin(`/plantas/${plantaId}/avisos/vista-previa`));
        } catch (err) {
            enqueueSnackbar(err.message, { variant: 'error' });
        } finally {
            setCargandoPreview(false);
        }
    }

    // ── Estilos ────────────────────────────────────────────────────────────────
    const celda = { py: 0.5, px: 1, fontSize: 12 };
    const inputSx = { '& .MuiInputBase-input': { fontSize: 12, py: 0.7 } };
    const titulo = { fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 1 };

    // Celda de casilla: solo aparece si el campo tiene algo escrito
    const casilla = (visible, checked, onChange) => (
        <TableCell sx={celda} align="center">
            {visible ? <Checkbox size="small" checked={checked} onChange={(e) => onChange(e.target.checked)} /> : null}
        </TableCell>
    );

    return (
        <>
            <FormControl size="small" sx={{ minWidth: 220, mb: 2 }}>
                <Select value={plantaId} onChange={(e) => setPlantaId(e.target.value)} sx={{ fontSize: 13 }}>
                    {plantas.map((p) => (
                        <MenuItem key={p.id} value={p.id} sx={{ fontSize: 13 }}>{p.nombre}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            {loading ? (
                <Stack alignItems="center" py={4}><CircularProgress size={28} /></Stack>
            ) : error ? (
                <Alert severity="error">{error}</Alert>
            ) : (
                <>
                    {/* ── Horario ─────────────────────────────────────────── */}
                    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                        <Typography variant="caption" color="text.disabled" sx={titulo}>Envío diario</Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                            <FormControlLabel
                                control={
                                    <Switch size="small" checked={config.activo}
                                        onChange={(e) => setConfig((c) => ({ ...c, activo: e.target.checked, _dirty: true }))} />
                                }
                                label={<Typography fontSize={13}>Avisos activos</Typography>}
                            />
                            <TextField
                                size="small" type="time" label="Hora de envío" value={config.hora}
                                onChange={(e) => setConfig((c) => ({ ...c, hora: e.target.value, _dirty: true }))}
                                error={!horaValida}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ step: 60 }}
                                sx={{ width: 150 }}
                            />
                            <Button variant="contained" size="small" startIcon={<SaveIcon />}
                                disabled={!config._dirty || !horaValida} onClick={guardarConfig}>
                                Guardar
                            </Button>
                        </Stack>
                        <Typography variant="caption" color="text.disabled" display="block" mt={1} fontSize={10}>
                            Hora de Argentina. Todos los días a esa hora se envía el estado de los tanques a los destinatarios activos.
                        </Typography>
                    </Paper>

                    {/* ── Destinatarios ───────────────────────────────────── */}
                    <Typography variant="caption" color="text.disabled" sx={titulo}>Destinatarios</Typography>
                    <Paper variant="outlined" sx={{ overflowX: 'auto', mb: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={celda} align="center">Activo</TableCell>
                                    <TableCell sx={celda}>Nombre</TableCell>
                                    <TableCell sx={celda}>Celular</TableCell>
                                    <TableCell sx={celda} align="center">SMS</TableCell>
                                    <TableCell sx={celda}>Mail</TableCell>
                                    <TableCell sx={celda} align="center">Mail</TableCell>
                                    <TableCell sx={celda} align="right"></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {dest.map((d) => (
                                    <TableRow key={d.id}>
                                        <TableCell sx={celda} align="center">
                                            <Switch size="small" checked={d.activo}
                                                onChange={(e) => editarDest(d.id, 'activo', e.target.checked)} />
                                        </TableCell>
                                        <TableCell sx={celda}>
                                            <TextField size="small" value={d.f_nombre}
                                                onChange={(e) => editarDest(d.id, 'f_nombre', e.target.value)}
                                                sx={{ ...inputSx, minWidth: 130 }} />
                                        </TableCell>
                                        <TableCell sx={celda}>
                                            <TextField size="small" value={d.f_celular} placeholder="+5491112345678"
                                                onChange={(e) => editarDest(d.id, 'f_celular', e.target.value)}
                                                sx={{ ...inputSx, minWidth: 140 }} />
                                        </TableCell>
                                        {casilla(d.f_celular.trim() !== '', d.recibe_sms, (v) => editarDest(d.id, 'recibe_sms', v))}
                                        <TableCell sx={celda}>
                                            <TextField size="small" value={d.f_email} placeholder="nombre@empresa.com"
                                                onChange={(e) => editarDest(d.id, 'f_email', e.target.value)}
                                                sx={{ ...inputSx, minWidth: 190 }} />
                                        </TableCell>
                                        {casilla(d.f_email.trim() !== '', d.recibe_mail, (v) => editarDest(d.id, 'recibe_mail', v))}
                                        <TableCell sx={{ ...celda, whiteSpace: 'nowrap' }} align="right">
                                            <Tooltip title="Guardar">
                                                <span>
                                                    <IconButton size="small" color="primary"
                                                        disabled={!d._dirty || !d.f_nombre.trim()}
                                                        onClick={() => guardarDest(d)}>
                                                        <SaveIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={d._dirty ? 'Guardá los cambios antes de probar' : 'Enviar prueba solo a este destinatario'}>
                                                <span>
                                                    <IconButton size="small" color="success"
                                                        disabled={d._dirty || !d.activo || enviando !== null}
                                                        onClick={() => probar(d.id)}>
                                                        {enviando === d.id ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Borrar">
                                                <IconButton size="small" color="error" onClick={() => setConfirmarBorrar(d)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}

                                {/* Alta */}
                                <TableRow sx={{ bgcolor: 'action.hover' }}>
                                    <TableCell sx={celda} align="center">
                                        <Switch size="small" checked={nuevo.activo}
                                            onChange={(e) => setNuevo((n) => aplicarCambio(n, 'activo', e.target.checked))} />
                                    </TableCell>
                                    <TableCell sx={celda}>
                                        <TextField size="small" value={nuevo.f_nombre} placeholder="Nombre"
                                            onChange={(e) => setNuevo((n) => aplicarCambio(n, 'f_nombre', e.target.value))}
                                            sx={{ ...inputSx, minWidth: 130 }} />
                                    </TableCell>
                                    <TableCell sx={celda}>
                                        <TextField size="small" value={nuevo.f_celular} placeholder="+5491112345678"
                                            onChange={(e) => setNuevo((n) => aplicarCambio(n, 'f_celular', e.target.value))}
                                            sx={{ ...inputSx, minWidth: 140 }} />
                                    </TableCell>
                                    {casilla(nuevo.f_celular.trim() !== '', nuevo.recibe_sms,
                                        (v) => setNuevo((n) => aplicarCambio(n, 'recibe_sms', v)))}
                                    <TableCell sx={celda}>
                                        <TextField size="small" value={nuevo.f_email} placeholder="nombre@empresa.com"
                                            onChange={(e) => setNuevo((n) => aplicarCambio(n, 'f_email', e.target.value))}
                                            sx={{ ...inputSx, minWidth: 190 }} />
                                    </TableCell>
                                    {casilla(nuevo.f_email.trim() !== '', nuevo.recibe_mail,
                                        (v) => setNuevo((n) => aplicarCambio(n, 'recibe_mail', v)))}
                                    <TableCell sx={celda} align="right">
                                        <Tooltip title="Agregar destinatario">
                                            <span>
                                                <IconButton size="small" color="primary" disabled={!puedeCrear} onClick={crearDest}>
                                                    <AddIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Paper>
                    <Typography variant="caption" color="text.disabled" display="block" mb={2} fontSize={10}>
                        Las casillas SMS y Mail aparecen cuando el campo tiene algo escrito. Un mail por destinatario.
                    </Typography>

                    {/* ── Acciones ────────────────────────────────────────── */}
                    <Stack direction="row" spacing={1} mb={2}>
                        <Button variant="outlined" size="small" startIcon={<PreviewIcon />}
                            disabled={cargandoPreview} onClick={verPreview}>
                            {cargandoPreview ? 'Armando…' : 'Vista previa'}
                        </Button>
                        <Button variant="outlined" size="small" color="success"
                            startIcon={enviando === 'todos' ? <CircularProgress size={14} /> : <SendIcon />}
                            disabled={enviando !== null || dest.length === 0}
                            onClick={() => probar(null)}>
                            Enviar prueba a todos
                        </Button>
                    </Stack>

                    {/* ── Registro de envíos ──────────────────────────────── */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="caption" color="text.disabled" sx={{ ...titulo, mb: 0 }}>
                            Últimos envíos
                        </Typography>
                        <Tooltip title="Actualizar">
                            <IconButton size="small" onClick={cargarEnvios} disabled={loadingEnv}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                    <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={celda}>Fecha</TableCell>
                                    <TableCell sx={celda}>Tipo</TableCell>
                                    <TableCell sx={celda}>Canal</TableCell>
                                    <TableCell sx={celda}>Destino</TableCell>
                                    <TableCell sx={celda}>Estado</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {envios.length === 0 ? (
                                    <TableRow>
                                        <TableCell sx={celda} colSpan={5}>
                                            <Typography fontSize={12} color="text.disabled">Sin envíos registrados.</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : envios.map((e) => (
                                    <TableRow key={e.id}>
                                        <TableCell sx={{ ...celda, whiteSpace: 'nowrap' }}>{fmtFecha(e.enviado)}</TableCell>
                                        <TableCell sx={celda}>{e.tipo === 'programado' ? 'Programado' : 'Prueba'}</TableCell>
                                        <TableCell sx={{ ...celda, textTransform: 'uppercase' }}>{e.canal}</TableCell>
                                        <TableCell sx={celda}>{e.destino}</TableCell>
                                        <TableCell sx={celda}>
                                            <Tooltip title={e.detalle || ''}>
                                                <Chip size="small" label={e.estado === 'ok' ? 'OK' : 'Error'}
                                                    color={e.estado === 'ok' ? 'success' : 'error'}
                                                    sx={{ fontSize: 10, height: 20 }} />
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                </>
            )}

            {/* Confirmación de borrado */}
            <Dialog open={!!confirmarBorrar} onClose={() => setConfirmarBorrar(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: 15 }}>¿Borrar destinatario?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        <b>{confirmarBorrar?.f_nombre}</b> deja de recibir los avisos de esta planta.
                        Si solo querés pausarlo, usá el interruptor "Activo".
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmarBorrar(null)}>Cancelar</Button>
                    <Button variant="contained" color="error" onClick={() => borrarDest(confirmarBorrar)}>Borrar</Button>
                </DialogActions>
            </Dialog>

            {/* Vista previa */}
            <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontSize: 15 }}>Vista previa del aviso</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="caption" color="text.disabled" sx={titulo}>
                        SMS ({preview?.sms.length} parte{preview?.sms.length === 1 ? '' : 's'})
                    </Typography>
                    {preview?.sms.map((parte, i) => (
                        <Paper key={i} variant="outlined" sx={{ p: 1, mb: 1 }}>
                            <Typography sx={{ fontFamily: "'Roboto Mono', monospace", fontSize: 12, whiteSpace: 'pre-wrap' }}>
                                {parte}
                            </Typography>
                            <Typography fontSize={10} color="text.disabled" align="right">{parte.length} / 160</Typography>
                        </Paper>
                    ))}
                    <Typography variant="caption" color="text.disabled" sx={{ ...titulo, mt: 2 }}>
                        Mail · {preview?.asunto}
                    </Typography>
                    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
                        <iframe title="Vista previa del mail" srcDoc={preview?.html || ''}
                            style={{ width: '100%', height: 520, border: 0 }} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreview(null)}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}